import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Composant de saisie SIRET avec validation en temps réel
 * 
 * @param {string} value - Valeur actuelle du SIRET
 * @param {function} onChange - Callback appelé lors du changement de valeur
 * @param {function} onValidation - Callback appelé après validation (format, isValid, companyData)
 * @param {function} onAutoComplete - Callback appelé quand les données INSEE sont disponibles
 * @param {string} label - Label du champ (défaut: "SIRET")
 * @param {boolean} required - Champ obligatoire
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} disabled - Désactiver le champ
 * @param {string} error - Message d'erreur externe
 */
export default function SIRETInput({
  value = '',
  onChange,
  onValidation,
  onAutoComplete,
  label = 'SIRET',
  required = false,
  className = '',
  disabled = false,
  error = null,
  showLabel = true
}) {
  const [validation, setValidation] = useState({
    isValidating: false,
    isValid: null,
    message: '',
    companyData: null
  });

  // Garde la trace du dernier SIRET validé et stabilise les callbacks
  // pour empêcher la re-validation à chaque render (qui réécrasait les
  // champs auto-remplis par l'utilisateur — bug forme_juridique).
  const lastValidatedRef = useRef('');
  const onValidationRef = useRef(onValidation);
  const onAutoCompleteRef = useRef(onAutoComplete);
  useEffect(() => { onValidationRef.current = onValidation; }, [onValidation]);
  useEffect(() => { onAutoCompleteRef.current = onAutoComplete; }, [onAutoComplete]);

  // Validation SIRET via API
  const validateSiret = useCallback(async (siret) => {
    const siretClean = siret?.replace(/\s/g, '') || '';
    
    // Ne pas valider si pas 14 chiffres
    if (siretClean.length !== 14 || !/^\d{14}$/.test(siretClean)) {
      setValidation({ isValidating: false, isValid: null, message: '', companyData: null });
      if (onValidationRef.current) onValidationRef.current({ isValid: null, message: '' });
      return;
    }
    
    // Skip si déjà validé pour ce SIRET (évite l'auto-fill répété qui
    // écrasait `forme_juridique` saisie manuellement par l'utilisateur)
    if (lastValidatedRef.current === siretClean) return;
    lastValidatedRef.current = siretClean;

    setValidation(prev => ({ ...prev, isValidating: true }));
    
    try {
      const token = getToken();
      const response = await axios.get(`${API}/siret/validate/${siretClean}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      const newValidation = {
        isValidating: false,
        isValid: data.format_valid,
        message: data.validation_message,
        companyData: data.company_data
      };
      
      setValidation(newValidation);
      
      // Callback de validation
      if (onValidationRef.current) {
        onValidationRef.current({
          isValid: data.format_valid,
          message: data.validation_message,
          companyData: data.company_data
        });
      }
      
      // Si données INSEE disponibles, callback d'auto-complétion
      if (data.company_data && onAutoCompleteRef.current) {
        onAutoCompleteRef.current(data.company_data);
      }
      
    } catch {
      // Validation locale en fallback
      const isValidFormat = siretClean.length === 14 && /^\d{14}$/.test(siretClean) && validateLuhn(siretClean);
      
      setValidation({
        isValidating: false,
        isValid: isValidFormat,
        message: isValidFormat ? 'Format SIRET valide' : 'Format SIRET invalide',
        companyData: null
      });
      
      if (onValidationRef.current) {
        onValidationRef.current({
          isValid: isValidFormat,
          message: isValidFormat ? 'Format SIRET valide' : 'Format SIRET invalide'
        });
      }
    }
  }, []);

  // Debounce pour la validation SIRET
  useEffect(() => {
    const siretClean = value?.replace(/\s/g, '') || '';
    if (siretClean.length === 14) {
      const timeoutId = setTimeout(() => {
        validateSiret(siretClean);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setValidation({ isValidating: false, isValid: null, message: '', companyData: null });
    }
  }, [value, validateSiret]);

  // Validation locale Luhn
  const validateLuhn = (siret) => {
    let total = 0;
    for (let i = 0; i < siret.length; i++) {
      let digit = parseInt(siret[i], 10);
      if (i % 2 === 0) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      total += digit;
    }
    return total % 10 === 0;
  };

  const handleChange = (e) => {
    const newValue = e.target.value.replace(/\s/g, '');
    if (onChange) {
      onChange(newValue);
    }
  };

  // Déterminer la couleur de bordure
  const getBorderClass = () => {
    if (error) return 'border-red-500 bg-red-50';
    if (validation.isValid === true) return 'border-green-500 bg-green-50';
    if (validation.isValid === false) return 'border-red-500 bg-red-50';
    return 'focus:border-blue-500';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {showLabel && (
        <Label className="font-semibold flex items-center gap-1">
          {label} {required && <span className="text-red-500">*</span>}
          {validation.isValidating && (
            <Loader2 className="h-3 w-3 animate-spin text-blue-500 ml-1" />
          )}
          {!validation.isValidating && validation.isValid === true && (
            <CheckCircle className="h-4 w-4 text-green-500 ml-1" />
          )}
          {!validation.isValidating && validation.isValid === false && (
            <AlertCircle className="h-4 w-4 text-red-500 ml-1" />
          )}
        </Label>
      )}
      
      <div className="relative">
        <Input
          value={value}
          onChange={handleChange}
          placeholder="12345678901234"
          maxLength={14}
          disabled={disabled}
          className={`h-11 border-2 font-mono pr-10 ${getBorderClass()}`}
          data-testid="siret-input"
        />
        {validation.isValidating && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          </div>
        )}
      </div>
      
      {/* Message d'erreur externe */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      )}
      
      {/* Message de validation */}
      {validation.message && !error && (
        <p className={`text-xs flex items-center gap-1 ${validation.isValid ? 'text-green-600' : 'text-amber-600'}`}>
          {validation.isValid ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
          {validation.message}
        </p>
      )}
    </div>
  );
}

// Export de la fonction de validation locale pour usage externe
export const validateSiretFormat = (siret) => {
  const siretClean = siret?.replace(/\s/g, '') || '';
  
  if (siretClean.length !== 14) {
    return { isValid: false, message: `Le SIRET doit contenir 14 chiffres (actuellement: ${siretClean.length})` };
  }
  
  if (!/^\d{14}$/.test(siretClean)) {
    return { isValid: false, message: 'Le SIRET ne doit contenir que des chiffres' };
  }
  
  // Validation Luhn
  let total = 0;
  for (let i = 0; i < siretClean.length; i++) {
    let digit = parseInt(siretClean[i], 10);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    total += digit;
  }
  
  if (total % 10 !== 0) {
    return { isValid: false, message: 'Numéro SIRET invalide (chiffre de contrôle incorrect)' };
  }
  
  return { isValid: true, message: 'Format SIRET valide' };
};
